import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import toolsData from './toolsData';
import { OddsConverter } from './calculators/OddsConverter';
import { DevigCalculator } from './calculators/DevigCalculator';
import { EvCalculator } from './calculators/EvCalculator';
import { KellyCriterionCalculator } from './calculators/KellyCriterionCalculator';
import { ParlayCalculator } from './calculators/ParlayCalculator';
import { ArbitrageCalculator } from './calculators/ArbitrageCalculator';
import { HoldCalculator } from './calculators/HoldCalculator';
import { ImpliedProbabilityCalculator } from './calculators/ImpliedProbabilityCalculator';
import { HedgeCalculator } from './calculators/HedgeCalculator';
import './Tools.css';

const CALCULATOR_MAP = {
	'odds-converter': OddsConverter,
	'devig-calculator': DevigCalculator,
	'ev-calculator': EvCalculator,
	'kelly-criterion-calculator': KellyCriterionCalculator,
	'parlay-calculator': ParlayCalculator,
	'arbitrage-calculator': ArbitrageCalculator,
	'hold-calculator': HoldCalculator,
	'implied-probability-calculator': ImpliedProbabilityCalculator,
	'hedge-calculator': HedgeCalculator,
};

export const ToolPage = () => {
	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const slug = window.location.pathname.replace('/tools/', '');
	const tool = toolsData.find(t => t.slug === slug);

	if (!tool) {
		return (
			<div className="tool-page-container">
				<div style={{ textAlign: 'center', paddingTop: '4rem' }}>
					<h1 style={{ color: '#ffffff', fontSize: '2rem', marginBottom: '1rem' }}>Tool Not Found</h1>
					<p style={{ color: '#9ca3af', marginBottom: '2rem' }}>The calculator you're looking for doesn't exist.</p>
					<button className="tool-page-back" onClick={() => { window.location.href = '/tools'; }}>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
						Back to Tools
					</button>
				</div>
			</div>
		);
	}

	const CalculatorComponent = CALCULATOR_MAP[slug];

	const appSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebApplication',
		name: tool.title,
		description: tool.description,
		url: `https://www.thewagerwiz.com/tools/${tool.slug}`,
		applicationCategory: 'FinanceApplication',
		operatingSystem: 'Any',
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'USD',
		},
		publisher: {
			'@type': 'Organization',
			name: 'WagerWiz',
			url: 'https://www.thewagerwiz.com',
		},
	};

	const faqSchema = tool.faqs && tool.faqs.length > 0 ? {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: tool.faqs.map(item => ({
			'@type': 'Question',
			name: item.q,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.a,
			},
		})),
	} : null;

	return (
		<div className="tool-page-container">
			<Helmet>
				<title>{tool.metaTitle}</title>
				<meta name="description" content={tool.metaDescription} />
				<link rel="canonical" href={`https://www.thewagerwiz.com/tools/${tool.slug}`} />
				<meta property="og:title" content={tool.metaTitle} />
				<meta property="og:description" content={tool.metaDescription} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={`https://www.thewagerwiz.com/tools/${tool.slug}`} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={tool.metaTitle} />
				<meta name="twitter:description" content={tool.metaDescription} />
				<script type="application/ld+json">{JSON.stringify(appSchema)}</script>
				{faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
			</Helmet>

			<button className="tool-page-back" onClick={() => { window.location.href = '/tools'; }}>
				<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
				Back to Tools
			</button>

			<div className="tool-page-article">
				<h1 className="tool-page-title">{tool.title}</h1>
				<p className="tool-page-description">{tool.description}</p>
				{CalculatorComponent && <CalculatorComponent />}
			</div>

			<div className="tool-cta">
				<h3>Find Your Edge with WagerWiz</h3>
				<p>
					Stop guessing. Use data-driven tools to find +EV bets, arbitrage opportunities, and the best odds across sportsbooks.
				</p>
				<button className="tool-cta-btn" onClick={() => { window.location.href = '/ev-scanner'; }}>
					Try Free EV Scanner
				</button>
			</div>
		</div>
	);
};
